import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const profileSchema = z.object({
  first_name: z.string().min(1, 'Le prénom est requis'),
  last_name: z.string().min(1, 'Le nom est requis'),
  phone: z.string().optional(),
  current_password: z.string().optional(),
  new_password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').optional(),
  confirm_password: z.string().optional()
}).refine((data) => {
  // Si un nouveau mot de passe est fourni, le mot de passe actuel est requis
  if (data.new_password && !data.current_password) {
    return false;
  }
  return true;
}, {
  message: "Le mot de passe actuel est requis pour changer le mot de passe",
  path: ["current_password"]
}).refine((data) => {
  // Les mots de passe doivent correspondre uniquement si un nouveau mot de passe est fourni
  if (data.new_password && data.new_password !== data.confirm_password) {
    return false;
  }
  return true;
}, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirm_password"]
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface UserProfileModalProps {
  onClose: () => void;
}

export default function UserProfileModal({ onClose }: UserProfileModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone')
        .eq('id', user?.id)
        .single();

      return {
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
        phone: profile?.phone || ''
      };
    }
  });

  const newPassword = watch('new_password');

  async function onSubmit(data: ProfileFormData) {
    try {
      setIsSubmitting(true);

      // Mise à jour du profil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      // Mise à jour du mot de passe si nécessaire
      if (showPasswordFields && data.new_password && data.current_password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: data.new_password
        });

        if (passwordError) throw passwordError;
      }

      addNotification('success', 'Profil mis à jour avec succès');
      onClose();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      addNotification('error', error.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('common.settings')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('users.form.firstName')}
              </label>
              <input
                type="text"
                {...register('first_name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.first_name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('users.form.lastName')}
              </label>
              <input
                type="text"
                {...register('last_name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.last_name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('common.phone')}
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div className="border-t dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('common.changePassword')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPasswordFields(!showPasswordFields)}
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                >
                  {showPasswordFields ? 'Annuler' : 'Modifier le mot de passe'}
                </button>
              </div>

              {showPasswordFields && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('common.currentPassword')}
                    </label>
                    <input
                      type="password"
                      {...register('current_password')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {errors.current_password && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.current_password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('common.newPassword')}
                    </label>
                    <input
                      type="password"
                      {...register('new_password')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    {errors.new_password && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.new_password.message}
                      </p>
                    )}
                  </div>

                  {newPassword && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('common.confirmPassword')}
                      </label>
                      <input
                        type="password"
                        {...register('confirm_password')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      {errors.confirm_password && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {errors.confirm_password.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}